/**
 * Mention trigger, candidate, and token helpers for composers and display.
 */

export const ALL_MENTION_CANDIDATE = {
  id: 0,
  name: "all",
  pfp: "",
  is_org: false,
  is_all: true,
  subtitle: "Everyone in this chat",
};

const MENTION_TOKEN_RE = /@\[(all|user:(\d+)|org:(\d+))\]/g;

/**
 * Find an @mention query at the caret (after start or whitespace).
 * @param {string} text
 * @param {number} caret
 * @returns {{ query: string, start: number, end: number } | null}
 */
export function findMentionTrigger(text, caret) {
  if (typeof text !== "string" || caret == null || caret < 1) {
    return null;
  }
  const before = text.slice(0, caret);
  const match = before.match(/(^|[\s])@([^\s]*)$/);
  if (!match) {
    return null;
  }
  const query = match[2];
  const start = before.length - query.length - 1;
  return { query, start, end: caret };
}

/**
 * Normalize a user, org, or chat member into a mention candidate.
 * @param {object} item
 * @returns {{ id: number, name: string, pfp: string, is_org: boolean, username: string, is_all?: boolean, subtitle?: string } | null}
 */
export function normalizeMentionCandidate(item) {
  if (!item) {
    return null;
  }
  if (item.is_all) {
    return { ...ALL_MENTION_CANDIDATE, ...item };
  }
  const isOrg = item.is_org === true || item.type === "organization";
  // Chat members use `id` as the membership row; mention tokens need the account id.
  const id = item.user_id ?? item.id;
  if (id == null) {
    return null;
  }
  const name = isOrg
    ? item.name || item.username || "Organization"
    : item.name ||
      `${item.first_name || ""} ${item.last_name || ""}`.trim() ||
      item.username ||
      "User";
  return {
    id: Number(id),
    name,
    pfp: item.pfp || item.pfp_url || "",
    is_org: isOrg,
    username: item.username || "",
  };
}

/**
 * Filter candidates by name or username.
 * @param {Array<object>} candidates
 * @param {string} query
 * @param {number} [limit=8]
 * @returns {Array<object>}
 */
export function filterMentionCandidates(candidates, query, limit = 8) {
  const list = Array.isArray(candidates) ? candidates : [];
  const q = (query || "").trim().toLowerCase();
  const filtered = q
    ? list.filter((candidate) => {
        const name = (candidate.name || "").toLowerCase();
        const username = (candidate.username || "").toLowerCase();
        return name.includes(q) || username.includes(q);
      })
    : list;
  return filtered.slice(0, limit);
}

/**
 * Replace the @query at the caret with `@Name` and record the mention range.
 * @param {string} text
 * @param {{ start: number, end: number }} trigger
 * @param {object} candidate
 * @returns {{ text: string, caret: number, mention: object }}
 */
export function insertMention(text, trigger, candidate) {
  const display = `@${candidate.name}`;
  const before = text.slice(0, trigger.start);
  const after = text.slice(trigger.end);
  const nextText = `${before}${display} ${after}`;
  const mention = {
    id: candidate.id,
    name: candidate.name,
    is_org: Boolean(candidate.is_org),
    is_all: Boolean(candidate.is_all),
    start: trigger.start,
    end: trigger.start + display.length,
  };
  return {
    text: nextText,
    caret: trigger.start + display.length + 1,
    mention,
  };
}

/**
 * Prefill composer text with `@Name ` and a tracked mention range.
 * @param {object} candidate
 * @returns {{ text: string, mentions: Array<object>, caret: number }}
 */
export function createMentionPrefill(candidate) {
  if (!candidate || (candidate.id == null && !candidate.is_all)) {
    return { text: "", mentions: [], caret: 0 };
  }
  const inserted = insertMention("", { start: 0, end: 0 }, candidate);
  return {
    text: inserted.text,
    mentions: [inserted.mention],
    caret: inserted.caret,
  };
}

/**
 * Re-bind mention ranges after an edit. Drops mentions whose `@Name` text is gone.
 * @param {string} text
 * @param {Array<object>} mentions
 * @returns {Array<object>}
 */
export function resyncMentions(text, mentions) {
  if (!text || !Array.isArray(mentions) || mentions.length === 0) {
    return [];
  }
  const usedStarts = new Set();
  const next = [];
  for (const mention of mentions) {
    const needle = `@${mention.name}`;
    let from = 0;
    let idx = -1;
    while (from <= text.length) {
      idx = text.indexOf(needle, from);
      if (idx === -1) {
        break;
      }
      if (!usedStarts.has(idx)) {
        usedStarts.add(idx);
        next.push({
          ...mention,
          start: idx,
          end: idx + needle.length,
        });
        break;
      }
      from = idx + 1;
    }
  }
  return next;
}

/**
 * Split composer display text into plain and mention segments for the highlight overlay.
 * @param {string} text
 * @param {Array<{ start: number, end: number }>} mentions
 * @returns {Array<{ type: "text"|"mention", value: string }>}
 */
export function getDisplayMentionParts(text, mentions) {
  if (!text) {
    return [];
  }
  const ranges = (mentions || [])
    .filter(
      (mention) =>
        Number.isFinite(mention.start) &&
        Number.isFinite(mention.end) &&
        mention.end > mention.start
    )
    .sort((a, b) => a.start - b.start);
  /** @type {Array<{ type: "text"|"mention", value: string }>} */
  const parts = [];
  let cursor = 0;
  for (const range of ranges) {
    const start = Math.max(0, Math.min(range.start, text.length));
    const end = Math.max(start, Math.min(range.end, text.length));
    if (start < cursor) {
      continue;
    }
    if (start > cursor) {
      parts.push({ type: "text", value: text.slice(cursor, start) });
    }
    parts.push({ type: "mention", value: text.slice(start, end) });
    cursor = end;
  }
  if (cursor < text.length) {
    parts.push({ type: "text", value: text.slice(cursor) });
  }
  return parts;
}

/**
 * Replace tracked `@Name` ranges with stable tokens for storage.
 * @param {string} text
 * @param {Array<object>} mentions
 * @returns {string}
 */
export function serializeMentionText(text, mentions) {
  if (!text) {
    return "";
  }
  if (!Array.isArray(mentions) || mentions.length === 0) {
    return text;
  }
  const sorted = [...mentions].sort((a, b) => b.start - a.start);
  let next = text;
  for (const mention of sorted) {
    const expected = `@${mention.name}`;
    if (next.slice(mention.start, mention.end) !== expected) {
      continue;
    }
    let token = `@[user:${mention.id}]`;
    if (mention.is_all) {
      token = "@[all]";
    } else if (mention.is_org) {
      token = `@[org:${mention.id}]`;
    }
    next = `${next.slice(0, mention.start)}${token}${next.slice(mention.end)}`;
  }
  return next;
}

/**
 * @typedef {Object} MentionBodyToken
 * @property {"text"|"mention"} type
 * @property {string} value
 * @property {"all"|"user"|"org"} [kind]
 * @property {number} [id]
 */

/**
 * Split stored text into plain text and mention tokens.
 * @param {string} text
 * @returns {MentionBodyToken[]}
 */
export function splitMentionTokens(text) {
  if (!text) {
    return [];
  }
  /** @type {MentionBodyToken[]} */
  const tokens = [];
  let cursor = 0;
  const regex = new RegExp(MENTION_TOKEN_RE.source, "g");
  let match = regex.exec(text);
  while (match) {
    if (match.index > cursor) {
      tokens.push({ type: "text", value: text.slice(cursor, match.index) });
    }
    const raw = match[0];
    if (match[1] === "all") {
      tokens.push({ type: "mention", value: raw, kind: "all", id: 0 });
    } else if (match[2]) {
      tokens.push({
        type: "mention",
        value: raw,
        kind: "user",
        id: Number(match[2]),
      });
    } else if (match[3]) {
      tokens.push({
        type: "mention",
        value: raw,
        kind: "org",
        id: Number(match[3]),
      });
    } else {
      tokens.push({ type: "text", value: raw });
    }
    cursor = match.index + raw.length;
    match = regex.exec(text);
  }
  if (cursor < text.length) {
    tokens.push({ type: "text", value: text.slice(cursor) });
  }
  return tokens;
}

/**
 * Build a lookup map from hydrated mention payloads.
 * @param {Array<object>} mentions
 * @returns {Map<string, object>}
 */
export function mentionLookup(mentions) {
  const map = new Map();
  for (const mention of mentions || []) {
    if (mention?.is_all) {
      map.set("all", mention);
      continue;
    }
    const kind = mention?.is_org ? "org" : "user";
    if (mention?.id != null) {
      map.set(`${kind}:${mention.id}`, mention);
    }
  }
  return map;
}

/**
 * Replace stored mention tokens with @Name for list and search previews.
 * Unresolved tokens fall back to @all / @user / @org (never raw @[user:id]).
 *
 * @param {string} text
 * @param {Array<object>} [mentions]
 * @returns {string}
 */
export function previewMentionDisplayText(text, mentions) {
  if (!text) {
    return "";
  }
  const lookup = mentionLookup(mentions);
  return splitMentionTokens(text)
    .map((part) => {
      if (part.type !== "mention") {
        return part.value;
      }
      const key =
        part.kind === "all" ? "all" : `${part.kind}:${part.id}`;
      const name = lookup.get(key)?.name;
      if (name) {
        return `@${name}`;
      }
      if (part.kind === "all") {
        return "@all";
      }
      return `@${part.kind}`;
    })
    .join("");
}

/**
 * Fetcher for posts, comments, and events: connections when the query is empty,
 * then users and orgs from search.
 * @param {number} userId
 * @returns {(query: string) => Promise<Array<object>>}
 */
export function createMentionFetcher(userId) {
  return async (query) => {
    if (!userId) {
      return [];
    }
    const { getMentionCandidates, searchMentionTargets } = await import("../api/api");
    const trimmed = (query || "").trim();
    const res = trimmed
      ? await searchMentionTargets(userId, trimmed)
      : await getMentionCandidates(userId);
    if (!res?.success || !Array.isArray(res.data)) {
      return [];
    }
    return res.data
      .map(normalizeMentionCandidate)
      .filter(Boolean)
      .filter((candidate) => Number(candidate.id) !== Number(userId));
  };
}
